/**
 * L'exercice tel que l'élève le verra.
 *
 * Ce composant reçoit le type `ExercisePreview` — celui que le serveur
 * construit **sans** corrigé. Il ne filtre rien lui-même, et c'est le point :
 * un aperçu qui masquerait `is_correct` à l'affichage aurait la bonne réponse
 * dans son état React, donc dans l'onglet réseau du navigateur, donc entre les
 * mains du premier élève curieux.
 *
 * Les champs de réponse sont **désactivés** : c'est une prévisualisation, pas
 * une tentative. Passer l'exercice arrive en phase 5, avec `learning`.
 */
import { useTranslation } from "react-i18next";

import type { ExercisePreview as ExercisePreviewData } from "@/features/catalog/api/useCatalog";
import { asDocument } from "@/shared/richtext/document";
import { RichContent } from "@/shared/richtext/RichContent";

interface ExercisePreviewProps {
  preview: ExercisePreviewData;
}

export function ExercisePreview({ preview }: ExercisePreviewProps) {
  const { t } = useTranslation("catalog");

  return (
    <section aria-label={t("exercises.previewTitle")} className="space-y-4">
      <header className="space-y-1">
        <h3 className="text-lg font-semibold">{preview.title}</h3>
        {preview.instructions !== null && (
          <div className="text-sm text-slate-600 dark:text-slate-300">
            <RichContent document={asDocument(preview.instructions)} />
          </div>
        )}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("exercises.previewMeta", {
            score: preview.passing_score,
            total: preview.max_score,
          })}
          {preview.is_blocking && ` · ${t("exercises.blockingNotice")}`}
          {preview.max_attempts !== null &&
            ` · ${t("exercises.attemptsNotice", { count: preview.max_attempts })}`}
        </p>
      </header>

      {preview.questions.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">{t("exercises.previewEmpty")}</p>
      )}

      <ol className="space-y-4">
        {preview.questions.map((question) => (
          <li
            key={question.id}
            className="rounded-md border border-slate-200 p-3 dark:border-slate-700"
          >
            <div className="mb-2 flex gap-1 text-sm font-medium">
              <span>{question.position}.</span>
              <RichContent document={asDocument(question.prompt)} />
            </div>
            {question.choices.length > 0 ? (
              <ul className="space-y-1">
                {question.choices.map((choice) => (
                  <li key={choice.id} className="flex items-center gap-2 text-sm">
                    <input
                      type={question.type === "multiple_choice" ? "checkbox" : "radio"}
                      name={`preview-${question.id}`}
                      disabled
                      aria-label={choice.label}
                    />
                    <span>{choice.label}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <input
                className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800"
                disabled
                placeholder={t("exercises.previewAnswerPlaceholder")}
                aria-label={t("exercises.previewAnswerPlaceholder")}
              />
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
